import React from "react"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import CircularProgress from "@material-ui/core/CircularProgress"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import IconButton from "@material-ui/core/IconButton"
import Paper from "@material-ui/core/Paper"
import Table from "@material-ui/core/Table"
import TableBody from "@material-ui/core/TableBody"
import TableCell from "@material-ui/core/TableCell"
import TableContainer from "@material-ui/core/TableContainer"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import { Mail, X } from "react-feather"
import { useTranslation } from "react-i18next"
import { errorToMessage } from "../../../utils/errors"
import { useHourReportTranslations } from "../../hooks/use-hour-report-translations"
import type { RequestReadyForCheckReportSummary } from "./RequestReadyForCheckDialog"

const defaultTranslations = {
  title: "Send reminder to mark time entry as ready for approval",
  description:
    "This time entry cannot be approved until the reviewer marks it as ready for approval. You can send an email reminder to the reviewer to mark it as ready for approval.",
}

type RequestReadyForApprovalDialogProps = {
  report: RequestReadyForCheckReportSummary | null
  onClose: () => void
  onRequestReadyForApproval: (reportId: string) => Promise<boolean | void>
  requestReadyForApprovalState: {
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    error?: unknown
  }
}

const RequestReadyForApprovalDialog = ({
  report,
  onClose,
  onRequestReadyForApproval,
  requestReadyForApprovalState,
}: RequestReadyForApprovalDialogProps) => {
  const classes = useStyles()
  const { t: typedT } = useTranslation("hour-report")
  const t = typedT as unknown as (key: string, options: Record<string, unknown>) => string
  const { dialogs, notSelected } = useHourReportTranslations()
  const [hasRequestedReminder, setHasRequestedReminder] = React.useState(false)

  React.useEffect(() => {
    setHasRequestedReminder(false)
  }, [report?.id])

  if (!report) return null

  const isSending = hasRequestedReminder && requestReadyForApprovalState.isLoading
  const isSent = hasRequestedReminder && requestReadyForApprovalState.isSuccess
  const isError = hasRequestedReminder && requestReadyForApprovalState.isError

  const handleRequestReadyForApproval = () => {
    setHasRequestedReminder(true)
    onRequestReadyForApproval(report.id)
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="ready-for-check-hour-report-approval-dialog-title"
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography id="ready-for-check-hour-report-approval-dialog-title">
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Typography variant="h4" className={classes.title}>
            {t("gridDialogs.readyForApprovalReminderTitle", { defaultValue: defaultTranslations.title })}
          </Typography>
          <IconButton aria-label={dialogs.close} onClick={onClose} size="small">
            <X size={20} className={classes.closeIcon} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box mt={2} mb={3}>
          <Typography variant="body1" color="textSecondary">
            {t("gridDialogs.readyForApprovalReminderDescription", {
              defaultValue: defaultTranslations.description,
            })}
          </Typography>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          aria-label={dialogs.summaryAriaLabel}
          className={classes.tableContainer}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>{dialogs.date}</TableCell>
                <TableCell className={classes.headerCell}>{dialogs.projectTask}</TableCell>
                <TableCell className={classes.headerCell}>{dialogs.hours}</TableCell>
                <TableCell className={classes.headerCell}>{dialogs.reportedBy}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.valueCell}>{String(report.date)}</TableCell>
                <TableCell className={classes.valueCell}>{report.projectOrTaskExportValue || notSelected}</TableCell>
                <TableCell className={classes.valueCell}>{report.hours}</TableCell>
                <TableCell className={classes.valueCell}>{report.reporterUserFullName}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {isSent ? (
          <Alert variant="filled" severity="success" className={classes.successAlert}>
            {dialogs.reminderSent}
          </Alert>
        ) : null}

        {isError ? (
          <Alert variant="filled" severity="error" className={classes.feedbackAlert}>
            {errorToMessage(requestReadyForApprovalState.error, dialogs.reminderError)}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant="outlined">
          {dialogs.close}
        </Button>
        {!isSent ? (
          <Button
            onClick={handleRequestReadyForApproval}
            color="primary"
            variant="contained"
            disabled={isSending}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <Mail size={16} />}
          >
            {isError ? dialogs.retrySending : dialogs.sendReminder}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 680,
  },
  title: {
    paddingTop: theme.spacing(0.5),
    paddingRight: theme.spacing(3),
    fontWeight: 600,
  },
  content: {
    paddingBottom: 0,
  },
  tableContainer: {
    background: theme.palette.background.default,
  },
  headerCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    textTransform: "uppercase",
  },
  valueCell: {
    paddingTop: 12,
    paddingBottom: 12,
    fontWeight: 600,
  },
  actions: {
    justifyContent: "flex-end",
    padding: theme.spacing(5, 3, 3),
  },
  closeIcon: {
    color: theme.palette.text.secondary,
  },
  feedbackAlert: {
    marginTop: theme.spacing(2),
  },
  successAlert: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.success.dark || theme.palette.success.main,
  },
}))

export default RequestReadyForApprovalDialog
